import React, { useEffect, useState } from "react";
import styles from "./profilePage.module.css";
import axios from "axios";
import PersonData from "../../../components/personData/PersonData";
import ImgComp from "../../../components/images/ImgComp";
import AddPost from "../../../components/addPost/AddPost";
import VideoComp from "../../../components/video/VideoComp";
import SearchComp from "../../../components/search/SearchComp";
import LanguagePalsComp from "../../../components/languagePals/LanguagePalsComp";
import PersonalPosts from "../../../components/personalPosts/PersonalPosts";
import { BiEdit } from "react-icons/bi";
import { useUser } from "../../../storage/UserContext"; // context hook
import defaultImage from "../../../appImges/avatartTemplate/default_img.png";

const apiUrl = process.env.REACT_APP_URL;

const ProfilePage = () => {
  /* A user's Profile Page contains personal information about the user, as well as his / her posts. A user can edit the personal data, change profile image, publish a new post, and also view, edit and delete previous posts by him / her. The user can also add comments to the posts shown on the page. In addition, the user can view his / her images, add / edit video URL, and use side components - Search and Pals. */

  const isEditable = true;
  const { userData, setUserData } = useUser(); // Using the context
  const [userImages, setUserImages] = useState([]);
  const [offset, setOffset] = useState(0);
  const qty = 5;
  const [profileData, setProfileData] = useState({
    img_link: "",
    username: "",
    first_name: "",
    last_name: "",
    country: "",
    city: "",
    birthdate: "",
    learned_language: "",
    native_language: "",
    description: "",
    education: "",
    hobbies: "",
    gender: "",
    video_link: "",
    role: "",
  });

  const [postsData, setPostsData] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const sessionRes = await axios.get(`${apiUrl}/auth/user`, {
          withCredentials: true,
        });

        if (sessionRes.status === 200 && sessionRes.data.success) {
          const username = sessionRes.data.username;

          const userRes = await axios.get(`${apiUrl}/users/${username}`, {
            withCredentials: true,
          });

          if (userRes.status === 200) {
            const profile = {
              img_link: userRes.data.img_link,
              username: userRes.data.username,
              first_name: userRes.data.first_name,
              last_name: userRes.data.last_name,
              country: userRes.data.country,
              city: userRes.data.city,
              birthdate: userRes.data.birthdate,
              learned_language: userRes.data.learned_language,
              native_language: userRes.data.native_language,
              description: userRes.data.description,
              education: userRes.data.education,
              hobbies: userRes.data.hobbies,
              gender: userRes.data.gender,
              video_link: userRes.data.video_link,
              role: userRes.data.role,
            };

            setProfileData(profile);
            setUserData((prevData) => ({
              ...prevData,
              ...profile,
              role: prevData.role, // Preserve existing role
            }));
          }
        }
      } catch (e) {
        console.error("Failed to fetch profile data", e);
      }
    };

    fetchProfileData();
  }, [setUserData]);

  // Fetch posts data
  const fetchPostsData = async (offset) => {
    try {
      const sessionRes = await axios.get(`${apiUrl}/auth/user`, {
        withCredentials: true,
      });

      if (sessionRes.status === 200 && sessionRes.data.success) {
        const username = sessionRes.data.username;

        const postsRes = await axios.get(
          `${apiUrl}/posts/shares_and_posts/${username}?qty=${qty}&offset=${offset}`,
          {
            withCredentials: true,
          }
        );

        if (postsRes.status === 200) {
          const newPosts = postsRes.data.posts;

          if (!newPosts || newPosts.length === 0) {
            return;
          }

          setPostsData((prevPosts) => {
            // Ensure no duplicates are added when updating the posts
            const filteredNewPosts = newPosts.filter(
              (newPost) =>
                !prevPosts.some((post) => post.post_id === newPost.post_id)
            );
            return [...prevPosts, ...filteredNewPosts];
          });

          // Check if there are more posts after the current batch
          if (newPosts.length === qty) {
            const morePostsRes = await axios.get(
              `${apiUrl}/posts/shares_and_posts/${username}?qty=1&offset=${
                offset + qty
              }`,
              {
                withCredentials: true,
              }
            );

            setHasMorePosts(morePostsRes.data.posts.length > 0);
          } else {
            setHasMorePosts(false);
          }
        } else {
          console.error("Failed to fetch posts:", postsRes.data.message);
        }
      } else {
        console.error("Failed to fetch session:", sessionRes.data.message);
      }
    } catch (e) {
      console.error("Failed to fetch posts data", e);
    }
  };

  useEffect(() => {
    fetchPostsData(offset);
  }, [offset]);

  // Fetch images data
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const sessionRes = await axios.get(`${apiUrl}/auth/user`, {
          withCredentials: true,
        });

        if (sessionRes.status === 200 && sessionRes.data.success) {
          const username = sessionRes.data.username;

          const imagesRes = await axios.get(
            `${apiUrl}/media/${username}?qty=10`,
            { withCredentials: true }
          );

          if (imagesRes.status === 200) {
            setUserData((prevData) => ({
              ...prevData,
              images: imagesRes.data.images,
              role: prevData.role, // Preserve existing role
            }));
            setUserImages(imagesRes.data.images);
          } else {
            console.error("Failed to fetch images:", imagesRes.data.message);
          }
        }
      } catch (e) {
        console.error("Failed to fetch images data", e);
      }
    };

    fetchImages();
  }, [setUserData]);

  // Update profile data function
  const handleUpdateProfileData = (updatedData) => {
    setProfileData((prevData) => ({
      ...prevData,
      ...updatedData,
    }));
    setUserData((prevData) => ({
      ...prevData,
      ...updatedData,
      role: prevData.role, // Ensure role is preserved
    }));
  };

  // Trigger the file input dialog when edit icon is clicked
  const handleEditProfilePhoto = () => {
    document.getElementById("fileInput").click();
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0]; // Get the selected file
    handleUpload(file); // Call the function to upload the file
  };

  // Handle image upload to the server
  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.put(
        `${apiUrl}/users/update_img/${profileData.username}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.status === 200 && response.data.success) {
        handleUpdateProfileData({ img_link: response.data.filePath });

        // Fetch updated images
        const imagesRes = await axios.get(
          `${apiUrl}/media/${profileData.username}?qty=10`,
          { withCredentials: true }
        );

        if (imagesRes.status === 200) {
          setUserData((prevData) => ({
            ...prevData,
            images: imagesRes.data.images,
            role: prevData.role, // Preserve existing role
          }));
          setUserImages(imagesRes.data.images);
        }
      } else {
        console.error("Failed to upload the image. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  // Handle "See More" button click
  const handleSeeMore = () => {
    setOffset((prevOffset) => prevOffset + qty);
  };

  // Callback function to update postsData when a new post is added
  const handleNewPost = (newPost) => {
    setPostsData((prevPosts) => [newPost, ...prevPosts]);
  };

  return (
    <div className={styles.main}>
      {/* hero section  */}
      <div className={styles.backgroundPic}>
        <figure className={styles.personPhoto}>
          <img
            alt={profileData.username}
            src={
              profileData.img_link &&
              profileData.img_link !== "default_img_link"
                ? profileData.img_link
                : defaultImage
            }
            onError={(e) => {
              e.target.src = defaultImage;
              e.target.onerror = null;
            }}
          />
          <figcaption>
            <span>
              {profileData.first_name} {profileData.last_name}
            </span>
          </figcaption>
          <BiEdit
            className={styles.editIcon}
            onClick={handleEditProfilePhoto}
          />
          <input
            type="file"
            id="fileInput"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </figure>
      </div>
      {/* person data section  */}
      <PersonData
        profileData={profileData}
        handleUpdateProfileData={handleUpdateProfileData}
        isEditable={isEditable}
      />

      {/* main layout  */}
      <section className={styles.mainLayout}>
        {/* left section  */}
        <div className={styles.left}>
          <ImgComp
            userImages={userImages}
            setUserImages={setUserImages}
            isEditable={isEditable}
            username={profileData.username}
          />
        </div>
        {/* middle section  */}
        <div className={styles.middle}>
          <AddPost
            onNewPost={handleNewPost}
            userImage={profileData.img_link}
            username={profileData.username}
          />
          <PersonalPosts
            role={profileData.role}
            checkTheSameUser={true}
            postsData={postsData}
            username={profileData.username}
            userImage={profileData.img_link}
            isEditable={isEditable}
          />
          {hasMorePosts && postsData.length > 0 && (
            <button onClick={handleSeeMore} className={styles.seeMoreBtn}>
              See More
            </button>
          )}
        </div>

        {/* right section  */}
        <div className={styles.right}>
          <VideoComp
            video_url={profileData.video_link}
            username={profileData.username}
            handleUpdateProfileData={handleUpdateProfileData}
            isEditable={isEditable}
          />
          <SearchComp />
          <LanguagePalsComp isOwn={true} />
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
